import { useTranslation } from 'react-i18next';
import type { PricingStrategyProfileResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { ProfileComparisonSection } from './profileComparisonRows';
import { formatProfileValue } from './profileValueFormat';

interface Props {
  profiles: PricingStrategyProfileResponse[];
  sections: ProfileComparisonSection[];
  selectedProfileVersionId: string | null;
}

export function ProfileComparisonTable({ profiles, sections, selectedProfileVersionId }: Props) {
  const { t } = useTranslation();
  return (
    <div className="@4xl:block hidden">
      <table className="border-line w-full table-fixed border-collapse border text-sm">
        <thead className="bg-card-alt">
          <tr>
            <th scope="col" className="border-line w-[31%] border p-3 text-left">
              {t('admin.dynamicPricing.profiles.rule')}
            </th>
            {profiles.map((profile) => (
              <th
                key={profile.versionId}
                scope="col"
                className={cn(
                  'border-line border p-3 text-left',
                  selectedProfileVersionId === profile.versionId && 'bg-sage/10',
                )}
              >
                {t(`admin.dynamicPricing.configuration.strategies.${profile.strategy}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((section) => (
            <SectionRows key={section.id} {...{ section, profiles, selectedProfileVersionId }} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionRows({
  section,
  profiles,
  selectedProfileVersionId,
}: Omit<Props, 'sections'> & { section: ProfileComparisonSection }) {
  const { t } = useTranslation();
  return (
    <>
      <tr className="bg-card-alt/60">
        <th
          colSpan={profiles.length + 1}
          className="border-line border px-3 py-2 text-left font-serif text-base"
        >
          {t(`admin.dynamicPricing.profiles.sections.${section.titleKey}`)}
        </th>
      </tr>
      {section.rows.map((row) => (
        <tr key={row.id}>
          <th scope="row" className="border-line border p-3 text-left font-medium">
            {t(`admin.dynamicPricing.profiles.rows.${row.detailKey}`)}
          </th>
          {profiles.map((profile, index) => (
            <td
              key={profile.versionId}
              className={cn(
                'border-line border p-3 tabular-nums',
                selectedProfileVersionId === profile.versionId && 'bg-sage/5',
              )}
            >
              {formatProfileValue(row.values[index] ?? null, row.format, t)}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
